<template>
  <div class="cyb-hotel-datepicker" :style="{ '--base-color': $theme.basecolor }">
    <div :class="['date-picker', disabled ? 'disabled' : '']">
      <div class="date-icon">
        <fa class="fa calendar" :style="{ color: $theme.basecolor }" :icon="['far', 'calendar-alt']" />
      </div>
      <div class="date-arrivo">
        <div class="label">{{ labels["check-in-label"] }}</div>
      </div>
      <div class="date-icon">
        <fa class="fa arrow" :style="{ color: $theme.basecolor }" :icon="['fas', 'chevron-right']" />
      </div>
      <div class="date-partenza">
        <div class="label">{{ labels["check-out-label"] }}</div>
      </div>
    </div>
    <hotel-date-picker
      ref="hoteldate"
      format="DD/MM/YYYY"
      :display-clear-button="false"
      :show-single-month="true"
      :i18n="etichette"
      :starting-date-value="dataArrivoIniziale"
      :ending-date-value="dataPartenzaIniziale"
      :show-price="mostraPrezzi"
      :price-symbol="simboloValuta"
      :style="{ 'pointer-events': disabled ? 'none' : 'all' }"
      @period-selected="getDate"
      @next-month-rendered="caricaMese"
    >
    </hotel-date-picker>
  </div>
</template>

<script>
import HotelDatePicker from "vue-hotel-datepicker";
import "vue-hotel-datepicker/dist/vueHotelDatepicker.css";
import moment from "moment";
import availabilityService from "../services/availability";

// @fortawesome/free-solid-svg-icons.
const SIMBOLI_VALUTA = {
  "euro-sign": "€",
  "dollar-sign": "$",
  "pound-sign": "£",
  "yen-sign": "¥",
  "lira-sign": "₺",
  "ruble-sign": "₽",
  "shekel-sign": "₪",
  "rupee-sign": "₹",
  "won-sign": "₩",
  hryvnia: "₴",
  tenge: "₸"
};

var labelHotelDatepicker = {
  night: "Notte",
  nights: "Notti",
  minStay: "Min %{nights} %{night}",
  soldOut: "Esaurito",
  closedToArrival: "No arrivo",
  closedToDeparture: "No partenza",
  "day-names": ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"],
  "check-in": "Arrivo",
  "check-out": "Partenza",
  "check-in-label": "Data di arrivo",
  "check-out-label": "Data di partenza",
  "month-names": ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"],
  tooltip: {
    minimumRequiredPeriod: "Minimo %{minNightInPeriod} %{night}"
  }
};

// decorazione applicata 1 volta sola
let decorato = false;

/**
 * Perché il giorno non è selezionabile, nella fase di scelta in corso
 *
 * @param {object} giorno il componente Day della libreria
 * @returns {string} "esaurito", "arrivo", "partenza" oppure "" se il giorno è disponibile
 */
function statoGiorno(giorno) {
  if (!giorno.belongsToThisMonth) {
    return "";
  }
  // l'esaurito prevale: la chiusura su una notte esaurita non aggiunge nulla
  if (availabilityService.soldOut(giorno.formatDate)) {
    return "esaurito";
  }
  const sceltaPartenza = !!giorno.checkIn && !giorno.checkOut;
  if (sceltaPartenza) {
    return availabilityService.closedToDeparture(giorno.formatDate) ? "partenza" : "";
  }
  return availabilityService.closedToArrival(giorno.formatDate) ? "arrivo" : "";
}

/** Cerca nel vnode la cella del giorno: è lei che ospita numero, minimo soggiorno e prezzo. */
function trovaCella(vnode) {
  if (!vnode) {
    return null;
  }
  if (vnode.data && vnode.data.staticClass === "vhd__datepicker__month-day-wrapper") {
    return vnode;
  }
  const figli = vnode.children || [];
  for (let i = 0; i < figli.length; i++) {
    const cella = trovaCella(figli[i]);
    if (cella) {
      return cella;
    }
  }
  return null;
}

export default {
  name: "CybHotelDatePicker",
  components: {
    HotelDatePicker
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    labels: {
      type: Object,
      default: () => {
        return labelHotelDatepicker;
      }
    }
  },
  data() {
    return {
      datahotel: {
        dataarrivo: "",
        datapartenza: "",
        notti: 0
      },
      dataArrivoIniziale: null,
      dataPartenzaIniziale: null,
      decorazioni: false
    };
  },
  computed: {
    /** Etichette per la libreria: quelle del chiamante arrivano dalle traduzioni e possono essere incomplete. */
    etichette() {
      const tooltip = Object.assign({}, labelHotelDatepicker.tooltip, this.labels.tooltip);
      return Object.assign({}, labelHotelDatepicker, this.labels, { tooltip: tooltip });
    },
    arrivoSorgente() {
      if (this.$store.form && this.$store.form.periodo.dataarrivo) {
        return this.$store.form.periodo.dataarrivo;
      }
      // arrivo da Google Hotel (vedi mixins/googleLanding)
      if (this.$route.query.gh === "1" && this.$route.query.arrivo) {
        return this.$route.query.arrivo;
      }
      if (this.$postData && this.$postData.da_data) {
        return this.$postData.da_data;
      }
      return "";
    },
    partenzaSorgente() {
      if (this.$store.form && this.$store.form.periodo.datapartenza) {
        return this.$store.form.periodo.datapartenza;
      }
      // arrivo da Google Hotel (vedi mixins/googleLanding)
      if (this.$route.query.gh === "1" && this.$route.query.partenza) {
        return this.$route.query.partenza;
      }
      if (this.$postData && this.$postData.a_data) {
        return this.$postData.a_data;
      }
      return "";
    },
    disponibilita() {
      return { db: this.$store.db, idhotel: this.$store.idhotel, attivo: this.$store.dispocalendario };
    },
    mostraPrezzi() {
      return this.decorazioni && !this.disabled && this.$store.dispocalendario;
    },
    simboloValuta() {
      return SIMBOLI_VALUTA[this.$theme.eurosign] || "€";
    }
  },
  watch: {
    arrivoSorgente: {
      immediate: true,
      handler(data) {
        this.dataArrivoIniziale = data ? moment(data, "DD/MM/YYYY").toDate() : moment().toDate();
      }
    },
    partenzaSorgente: {
      immediate: true,
      handler(data) {
        this.dataPartenzaIniziale = data ? moment(data, "DD/MM/YYYY").toDate() : moment().add(1, "day").toDate();
      }
    },
    disponibilita: {
      immediate: true,
      handler({ db, idhotel, attivo }) {
        availabilityService.setEnabled(attivo);
        if (!this.disabled) {
          availabilityService.preload(db, idhotel);
        }
      }
    }
  },
  created() {
    this.decorazioni = this.decoraGiorni();
  },
  mounted() {
    this.getDate(null, this.dataArrivoIniziale, this.dataPartenzaIniziale);
  },
  methods: {
    /**
     * Aggancia la disponibilità alle celle di vue-hotel-datepicker
     *
     * @returns {boolean} false se la struttura interna della libreria non è più quella attesa
     */
    decoraGiorni() {
      if (decorato) {
        return true;
      }
      const month = HotelDatePicker.components && HotelDatePicker.components.Month;
      const day = month && month.components && month.components.Day;
      if (!day || !day.render || !day.computed || !day.computed.dayPrice || !day.computed.dayClass || !day.computed.tooltipMessageDisplay) {
        // struttura cambiata: il calendario resta usabile, solo senza decorazioni
        console.warn("[cyb] vue-hotel-datepicker: impossibile decorare i giorni, struttura non riconosciuta");
        return false;
      }
      decorato = true;

      day.computed.dayPrice = function dayPrice() {
        return this.belongsToThisMonth ? availabilityService.price(this.formatDate) : "";
      };

      // alle classi della libreria si aggiunge solo il marcatore di giorno non disponibile:
      // esaurito e chiusura hanno stili diversi
      const dayClassBase = day.computed.dayClass;
      day.computed.dayClass = function dayClass() {
        const classi = dayClassBase.call(this);
        const stato = statoGiorno(this);
        if (stato === "esaurito") {
          return classi + " cyb-day--esaurito";
        }
        return stato ? classi + " cyb-day--chiuso" : classi;
      };

      // minimo soggiorno del giorno, o il motivo per cui non è selezionabile
      day.computed.minStayLabel = function minStayLabel() {
        const stato = statoGiorno(this);
        const motivo = { esaurito: this.i18n.soldOut, arrivo: this.i18n.closedToArrival, partenza: this.i18n.closedToDeparture }[stato];
        if (motivo) {
          return motivo;
        }
        const minimo = this.belongsToThisMonth ? availabilityService.minStay(this.formatDate) : 0;
        if (minimo < 2 || !this.i18n.minStay) {
          return "\u00a0";
        }
        return this.i18n.minStay.replace("%{nights}", minimo).replace("%{night}", this.i18n.nights.toLowerCase());
      };

      // la riga non esiste nel template della libreria
      const renderBase = day.render;
      day.render = function render(h) {
        const vnode = renderBase.call(this, h);
        const cella = trovaCella(vnode);
        if (cella) {
          // dopo il numero del giorno, prima del prezzo
          cella.children.splice(1, 0, h("span", { staticClass: "cyb-day-minstay" }, this.minStayLabel));
        }
        return vnode;
      };

      // il tooltip del soggiorno
      const tooltipBase = day.computed.tooltipMessageDisplay;
      day.computed.tooltipMessageDisplay = function tooltipMessageDisplay() {
        const testo = tooltipBase.call(this);
        // a soggiorno completo il tooltip non compare, e sul solo arrivo non c'è ancora periodo
        if (!testo || !this.checkIn || this.checkOut) {
          return testo;
        }
        const righe = [testo];
        const prezzo = availabilityService.stayPrice(this.dateFormater(this.checkIn), this.formatDate);
        if (prezzo) {
          righe.push(this.priceSymbol + " " + prezzo);
        }
        return righe.map((riga) => '<div class="cyb-tooltip-line">' + riga + "</div>").join("");
      };

      return true;
    },
    getDate(event, start, end) {
      this.datahotel = {
        dataarrivo: moment(start).format("DD/MM/YYYY"),
        datapartenza: moment(end).format("DD/MM/YYYY"),
        notti: moment(end).diff(start, "days")
      };
      this.$emit("input", this.datahotel);
    },
    clearDate() {
      this.datahotel = {
        dataarrivo: "",
        datapartenza: "",
        notti: 0
      };
      this.$emit("input", this.datahotel);
    },
    caricaMese(mese) {
      if (!this.disabled) {
        availabilityService.ensureMonth(this.$store.db, this.$store.idhotel, mese);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .vhd__datepicker {
  font-family: inherit;
  &.vhd__datepicker--open {
    box-shadow: 0 0 4px 2px #0003;
  }
  .vhd__datepicker__inner {
    padding: 0 1.5rem;
    .vhd__datepicker__month {
      margin-bottom: 24px;
      padding: 0;
      .vhd__datepicker__month-day--disabled span.day {
        text-decoration: none;
      }
      .vhd__square:has(.vhd__datepicker__month-day--valid) ~ .vhd__square:has(.vhd__datepicker__month-day--hidden) {
        display: none;
      }
    }
  }
}

// scaling pannello datepicker
@media (min-width: 768px) {
  ::v-deep .vhd__datepicker {
    left: calc(7.5% - 9px);
    right: calc(-17.5% - 22px);
    margin-left: auto;
    margin-right: auto;
    max-width: calc(117vh - 280px);
    &.vhd__datepicker--open {
      max-height: 150vh;
    }
    .vhd__datepicker__months--full {
      width: 100% !important;
    }
    .vhd__datepicker__month .vhd__datepicker__month-day-wrapper span.day {
      font-size: clamp(10px, calc((117vh - 280px) * 22 / 983), 18px);
    }
  }
}
@media (min-width: 1701px) {
  ::v-deep .vhd__datepicker {
    left: calc(7.2% - 9px);
    right: calc(-12.8% - 19px);
  }
}

// CSS mobile
@media (max-width: 767px) {
  ::v-deep .vhd__datepicker.vhd__datepicker--open {
    max-height: 85%;
    top: 7.5em;
    // scritte inutili
    .vhd__datepicker__input,
    .vhd__datepicker__tooltip--mobile {
      display: none;
    }
    .vhd__datepicker__inner {
      padding-inline: 0;
      // header - bottoni
      .vhd__datepicker__header-mobile {
        width: 100%;
        padding-top: 4em;
        padding-inline: 10px;
        display: flex;
        justify-content: space-between;
        .vhd__datepicker__month-button {
          width: 1em;
          height: 2.5em;
          border: none;
          position: relative;
          &::after {
            position: absolute;
            inset: 0;
            font-size: 2em;
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 999;
          }
          &.vhd__datepicker__month-button--prev::after {
            content: "‹";
          }
          &.vhd__datepicker__month-button--next::after {
            content: "›";
          }
        }
      }
      .vhd__datepicker__months {
        margin-top: 0em !important;
        padding-top: 7em;
        overflow: hidden !important;
        height: 100% !important;
        .vhd__datepicker__month {
          .vhd__datepicker__month-name {
            font-size: 18px;
          }
          .vhd__datepicker__week-row.vhd__hide-up-to-tablet {
            margin-top: 10px;
            display: block !important;
            box-shadow: none;
          }
          .vhd__datepicker__month-day--valid.vhd__datepicker__month-day--disabled {
            background-color: var(--base-color) !important;
            color: #ffffff;
            font-weight: 800;
          }
        }
      }
    }
  }
  // bottone x
  ::v-deep .vhd__datepicker__close-button.vhd__hide-on-desktop {
    transition: top 0.2s ease-in-out;
    top: 7.7em;
    right: 0.5em;
    font-size: 16px;
    width: 2em !important;
    padding-top: 10px;
    i {
      font-weight: 200;
      color: black;
      font-size: 2.1em;
    }
  }
}
@media (max-width: 576px) {
  ::v-deep .vhd__datepicker.vhd__datepicker--open {
    max-height: 80%;
    top: 10.8em;
  }
  ::v-deep .vhd__datepicker__close-button.vhd__hide-on-desktop {
    top: 11em;
  }
}
// CSS standard
@media (max-width: 593px) {
  .calendar,
  .arrow {
    font-size: 1.2em !important;
  }
}
.cyb-hotel-datepicker {
  position: static;
  height: 48px;
}
::v-deep .vhd__datepicker__wrapper {
  position: relative;
  background: none; // icona datepicker
  margin-top: -30px;
  top: -18px;
  height: 48px;
  .vhd__datepicker__dummy-wrapper {
    border: none;
    position: absolute;
    inset: 0;
    .vhd__datepicker__input {
      font-family: inherit;
      font-size: 1em;
      text-align: center;
      padding: 21px 0 0;
      font-weight: bold;
      height: 100%;
      line-height: inherit;
      word-spacing: inherit;
      &:first-child {
        background: none; // icona freccia
        padding-left: 26px;
      }
      &:last-child {
        padding-left: 12px;
      }
    }
  }
  .vhd__datepicker__tooltip {
    padding: 2px 5px;
    .cyb-tooltip-line {
      font: inherit;
      line-height: 1.3;
    }
  }
  .vhd__datepicker {
    margin-top: -5px !important;
    &.vhd__datepicker--open {
      .vhd__datepicker__header {
        display: flex;
        justify-content: space-between;
        .vhd__datepicker__month-button {
          background: transparent;
          border: none;
          &.vhd__datepicker__month-button--prev::after {
            color: #3d3d3d !important;
            font-size: 2em;
            content: "‹";
          }
          &.vhd__datepicker__month-button--next::after {
            color: #3d3d3d !important;
            font-size: 2em;
            content: "›";
          }
        }
      }
    }
  }
  // popup
  // TODO da fare quando si sa che colori mettere
  // .vhd__datepicker {
  //   // oggi
  //   .vhd__datepicker__month-day--today {}
  //   // checkin
  //   .vhd__datepicker__month-day--first-day-selected {}
  //   // middle
  //   .vhd__datepicker__month-day--selected {}
  //   // checkout
  //   .vhd__datepicker__month-day--last-day-selected {}
  // }
}
::v-deep .date-picker {
  display: flex;
  justify-content: space-between;
  height: 48px;
  .date-icon .fa.calendar {
    margin-left: 8px;
  }
  .date-partenza,
  .date-arrivo {
    flex-grow: 2;
    text-align: center;
  }
  .label {
    font-size: 14px;
  }
  .date {
    font-weight: bold;
  }
  .date-icon {
    align-self: center;
    &.fa {
      font-size: 24px;
    }
  }
  &.disabled {
    &.fa,
    .fa {
      color: gray !important;
    }
    div:active,
    .fa:active {
      filter: brightness(1) !important;
    }
  }
}
// ---------------- customize-datepicker-color --------------------
::v-deep .vhd__datepicker__month {
  .vhd__datepicker__month-day-wrapper {
    border-color: var(--base-color);
  }
  .vhd__datepicker__month-day--valid:hover {
    background-color: var(--base-color);
  }
  .vhd__datepicker__month-day--selected {
    background-color: color-mix(in srgb, var(--base-color), #fff 30%);
  }
  .vhd__datepicker__month-day--selected:hover {
    background-color: var(--base-color);
  }
  .vhd__datepicker__month-day.vhd__datepicker__month-day--first-day-selected,
  .vhd__datepicker__month-day.vhd__datepicker__month-day--last-day-selected {
    background: var(--base-color) !important;
  }
}
// ---------------- disponibilita-giornaliera --------------------
::v-deep .vhd__datepicker__month {
  .vhd__datepicker__month-day-wrapper {
    span.day {
      top: 28%;
      font-size: 18px;
      font-weight: normal;
      line-height: 1;
    }
    .cyb-day-minstay,
    .price {
      position: absolute;
      left: 0;
      width: 100%;
      text-align: center;
      z-index: 1;
      font-size: 11px;
      font-weight: normal;
      line-height: 1;
      white-space: nowrap;
      opacity: 0.64;
      box-sizing: border-box;
      padding: 0 2px;
      letter-spacing: -0.4px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .cyb-day-minstay {
      bottom: 30%;
      font-size: 10px;
      opacity: 0.42;
    }
    .price {
      top: auto;
      bottom: 10%;
      .price-symbol {
        margin-right: 2px;
      }
    }
  }
  // chiusura all'arrivo o alla partenza: la notte è in vendita, solo quel giorno non si sceglie
  .vhd__datepicker__month-day.cyb-day--chiuso {
    .vhd__datepicker__month-day-wrapper {
      background-color: #fff3dc;
      span {
        opacity: 0.64;
      }
      .cyb-day-minstay {
        color: #9a5b00;
        opacity: 1;
      }
    }
    // l'hover della libreria mette il testo bianco: sull'arancio chiaro non si leggerebbe
    &:hover {
      color: #3d3d3d;
      .vhd__datepicker__month-day-wrapper {
        background-color: #ffe4b3;
      }
    }
    // nel periodo selezionato vince il colore della selezione, testo compreso
    &.vhd__datepicker__month-day--selected,
    &.vhd__datepicker__month-day--first-day-selected,
    &.vhd__datepicker__month-day--last-day-selected {
      color: #ffffff;
      .vhd__datepicker__month-day-wrapper {
        background-color: transparent;
        span {
          opacity: 1;
        }
        .cyb-day-minstay {
          color: inherit;
          opacity: 0.8;
        }
      }
    }
  }
  // notte esaurita
  .vhd__datepicker__month-day.cyb-day--esaurito {
    .vhd__datepicker__month-day-wrapper {
      &::before {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        pointer-events: none;
        background: repeating-linear-gradient(
          -45deg,
          transparent 0,
          transparent 3px,
          rgba(60, 60, 60, 0.2) 3px,
          rgba(60, 60, 60, 0.2) 6px
        );
      }
      span {
        opacity: 0.64;
      }
    }
  }
}

// :::::::::::::::::::::::::::::::: COLORI ::::::::::::::::::::::::::::::::
// ---------------- base-gray --------------------
// ::v-deep .vhd__datepicker__month-button {
//   filter: saturate(0);
//   border-width: 2px;
//   &:hover {
//     opacity: 1;
//   }
//   &:active {
//     filter: saturate(0) brightness(0.5) !important;
//   }
// }
// ---------------- custom-vue --------------------
::v-deep .custom-vue .vhd__datepicker__month-button {
  // filter: hue-rotate(-15deg) saturate(0.57);
  filter: brightness(0) saturate(100%) invert(64%) sepia(17%) saturate(1382%) hue-rotate(101deg) brightness(97%) contrast(80%);
  &:active {
    filter: brightness(0) saturate(100%) invert(64%) sepia(17%) saturate(1382%) hue-rotate(101deg) brightness(97%) contrast(80%) brightness(0.5) !important;
  }
}
// ---------------- custom-red --------------------
::v-deep .custom-red .vhd__datepicker__month-button {
  filter: brightness(0) saturate(100%) invert(16%) sepia(77%) saturate(6761%) hue-rotate(6deg) brightness(108%) contrast(131%);
  &:active {
    filter: brightness(0) saturate(100%) invert(16%) sepia(77%) saturate(6761%) hue-rotate(6deg) brightness(108%) contrast(131%) brightness(0.5) !important;
  }
}
// ---------------- custom-rose --------------------
::v-deep .custom-rose .vhd__datepicker__month-button {
  filter: brightness(0) invert(19%) sepia(93%) saturate(7475%) hue-rotate(324deg) brightness(102%) contrast(104%);
  &:active {
    filter: brightness(0) invert(19%) sepia(93%) saturate(7475%) hue-rotate(324deg) brightness(102%) contrast(104%) brightness(0.5) !important;
  }
}
// ---------------- custom-magenta --------------------
::v-deep .custom-magenta .vhd__datepicker__month-button {
  filter: brightness(0) invert(31%) sepia(96%) saturate(6017%) hue-rotate(294deg) brightness(113%) contrast(130%);
  &:active {
    filter: brightness(0) invert(31%) sepia(96%) saturate(6017%) hue-rotate(294deg) brightness(113%) contrast(130%) brightness(0.5) !important;
  }
}
// ---------------- custom-violet --------------------
::v-deep .custom-violet .vhd__datepicker__month-button {
  filter: brightness(0) invert(12%) sepia(100%) saturate(7497%) hue-rotate(272deg) brightness(100%) contrast(124%);
  &:active {
    filter: brightness(0) invert(12%) sepia(100%) saturate(7497%) hue-rotate(272deg) brightness(100%) contrast(124%) brightness(0.5) !important;
  }
}
// ---------------- custom-blue --------------------
::v-deep .custom-blue .vhd__datepicker__month-button {
  filter: brightness(0) invert(9%) sepia(100%) saturate(6560%) hue-rotate(248deg) brightness(91%) contrast(147%);
  &:active {
    filter: brightness(0) invert(9%) sepia(100%) saturate(6560%) hue-rotate(248deg) brightness(91%) contrast(147%) brightness(0.5) !important;
  }
}
// ---------------- custom-azure --------------------
::v-deep .custom-azure .vhd__datepicker__month-button {
  filter: brightness(0) invert(29%) sepia(85%) saturate(2482%) hue-rotate(199deg) brightness(105%) contrast(104%);
  &:active {
    filter: brightness(0) invert(29%) sepia(85%) saturate(2482%) hue-rotate(199deg) brightness(105%) contrast(104%) brightness(0.5) !important;
  }
}
// ---------------- custom-cyan --------------------
::v-deep .custom-cyan .vhd__datepicker__month-button {
  filter: brightness(0) invert(83%) sepia(64%) saturate(2129%) hue-rotate(117deg) brightness(103%) contrast(103%);
  &:active {
    filter: brightness(0) invert(83%) sepia(64%) saturate(2129%) hue-rotate(117deg) brightness(103%) contrast(103%) brightness(0.5) !important;
  }
}
// ---------------- custom-mint --------------------
::v-deep .custom-mint .vhd__datepicker__month-button {
  filter: brightness(0) invert(89%) sepia(26%) saturate(6635%) hue-rotate(90deg) brightness(100%) contrast(104%);
  &:active {
    filter: brightness(0) invert(89%) sepia(26%) saturate(6635%) hue-rotate(90deg) brightness(100%) contrast(104%) brightness(0.5) !important;
  }
}
// ---------------- custom-green --------------------
::v-deep .custom-green .vhd__datepicker__month-button {
  filter: brightness(0) invert(70%) sepia(82%) saturate(5584%) hue-rotate(92deg) brightness(128%) contrast(118%);
  &:active {
    filter: brightness(0) invert(70%) sepia(82%) saturate(5584%) hue-rotate(92deg) brightness(128%) contrast(118%) brightness(0.5) !important;
  }
}
// ---------------- custom-lime --------------------
::v-deep .custom-lime .vhd__datepicker__month-button {
  filter: brightness(0) invert(72%) sepia(78%) saturate(963%) hue-rotate(40deg) brightness(110%) contrast(114%);
  &:active {
    filter: brightness(0) invert(72%) sepia(78%) saturate(963%) hue-rotate(40deg) brightness(110%) contrast(114%) brightness(0.5) !important;
  }
}
// ---------------- custom-yellow --------------------
::v-deep .custom-yellow .vhd__datepicker__month-button {
  filter: brightness(0) invert(82%) sepia(71%) saturate(616%) hue-rotate(1deg) brightness(108%) contrast(102%);
  &:active {
    filter: brightness(0) invert(82%) sepia(71%) saturate(616%) hue-rotate(1deg) brightness(108%) contrast(102%) brightness(0.5) !important;
  }
}
// ---------------- custom-orange --------------------
::v-deep .custom-orange .vhd__datepicker__month-button {
  filter: brightness(0) invert(61%) sepia(45%) saturate(5196%) hue-rotate(360deg) brightness(101%) contrast(107%);
  &:active {
    filter: brightness(0) invert(61%) sepia(45%) saturate(5196%) hue-rotate(360deg) brightness(101%) contrast(107%) brightness(0.5) !important;
  }
}
</style>
